import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Check,
  User,
  MapPin,
  Building,
  Briefcase,
  Calendar,
  Clock,
  Globe,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  TextInput,
  TextareaInput,
  SelectDropdown,
  type OptionItem,
} from "@/components/formfields";
import { useUpdateProfile } from "./sections/hooks/useProfile";
import { useInfiniteTeamList } from "@/pages/master-management/hooks/useTeam";
import { useInfiniteDepartmentList } from "@/pages/master-management/hooks/useDepartment";
import type { TeamDetail, DepartmentDetail } from "@/interface/masterManagement";
import type { ProfileDetail } from "@/interface/profile";

const getTodayDateString = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getProfileSchema = (isFromOther?: boolean) =>
  z
    .object({
      first_name: isFromOther
        ? z.string().optional()
        : z.string().trim().min(1, "First name is required"),
      last_name: isFromOther
        ? z.string().optional()
        : z.string().trim().min(1, "Last name is required"),
      team: z.string().optional(),
      department: z.string().optional(),
      job_role: z.string().trim().min(1, "Job Role is required"),
      birth_date: z.string().trim().optional().nullable(),
      experience_years: z.coerce
        .number()
        .min(0, "Experience years cannot be negative")
        .optional()
        .nullable(),
      experience_months: z.coerce
        .number()
        .min(0, "Months must be at least 0")
        .max(11, "Months must be at most 11")
        .optional()
        .nullable(),
      address: z.string().trim().optional(),
      city: z.string().trim().optional(),
      state: z.string().trim().optional(),
      country: z.string().trim().optional(),
      postal_code: z
        .string()
        .trim()
        .regex(/^\d*$/, "Postal code must contain only numbers")
        .optional()
        .nullable(),
      summary: z
        .string()
        .max(1000, "Summary must not exceed 1000 characters")
        .optional(),
    })
    .superRefine((data, ctx) => {
      const todayStr = getTodayDateString();
      if (data.birth_date && data.birth_date.trim() && data.birth_date > todayStr) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Date of Birth cannot be in the future",
          path: ["birth_date"],
        });
      }
    });

const profileSchema = getProfileSchema(false);

export type ProfileFormValues = z.infer<typeof profileSchema>;

interface ProfileEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  profileData?: ProfileDetail | null;
  source?: "profile" | "other";
}

export default function ProfileEditModal({
  isOpen,
  onClose,
  profileData,
  source = "profile",
}: ProfileEditModalProps) {
  const isFromOther = source === "other";
  const updateMutation = useUpdateProfile();
  const {
    data: teamData,
    isLoading: isTeamsLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteTeamList({ page_size: 20 });

  const teamOptions: OptionItem[] = useMemo(() => {
    if (!teamData?.pages) return [];
    return teamData.pages.flatMap((page) => {
      const items = Array.isArray(page?.data)
        ? page.data
        : page?.data?.results || [];
      return items
        .map((t: TeamDetail) => ({
          label: t.team_name || "",
          value: String(t.id),
        }))
        .filter((opt: OptionItem) => Boolean(opt.label));
    });
  }, [teamData]);

  const {
    data: departmentData,
    isLoading: isDepartmentsLoading,
    fetchNextPage: fetchNextDeptPage,
    hasNextPage: hasDeptNextPage,
    isFetchingNextPage: isFetchingDeptNextPage,
  } = useInfiniteDepartmentList({ page_size: 20 });

  const departmentOptions: OptionItem[] = useMemo(() => {
    if (!departmentData?.pages) return [];
    return departmentData.pages.flatMap((page) => {
      const items = Array.isArray(page?.data)
        ? page.data
        : page?.data?.results || [];
      return items
        .map((d: DepartmentDetail) => ({
          label: d.department_name || "",
          value: String(d.id),
        }))
        .filter((opt: OptionItem) => Boolean(opt.label));
    });
  }, [departmentData]);

  const currentSchema = useMemo(
    () => getProfileSchema(isFromOther),
    [isFromOther],
  );

  const { control, handleSubmit, reset, setValue } = useForm<ProfileFormValues>(
    {
      resolver: zodResolver(currentSchema) as any,
      defaultValues: {
        first_name: "",
        last_name: "",
        team: "",
        department: "",
        job_role: "",
        birth_date: "",
        experience_years: 0,
        experience_months: 0,
        address: "",
        city: "",
        state: "",
        country: "",
        postal_code: "",
        summary: "",
      },
    },
  );

  useEffect(() => {
    if (isOpen && profileData) {
      const teamVal = profileData.team_detail?.id ?? "";
      const deptVal = profileData.department_detail?.id ?? "";

      reset({
        first_name: profileData.first_name || "",
        last_name: profileData.last_name || "",
        team: teamVal ? String(teamVal) : "",
        department: deptVal ? String(deptVal) : "",
        job_role: profileData.job_role || "",
        birth_date: profileData.birth_date || "",
        experience_years: profileData.experience_years ?? 0,
        experience_months: profileData.experience_months ?? 0,
        address: profileData.address || "",
        city: profileData.city || "",
        state: profileData.state || "",
        country: profileData.country || "",
        postal_code: profileData.postal_code || "",
        summary: profileData.summary || "",
      });
    }
  }, [isOpen, profileData, reset]);

  const onSubmit = async (data: ProfileFormValues) => {
    try {
      const id = profileData?.id;
      if (id) {
        const payload = {
          ...data,
          ...(data.team ? { team: Number(data.team) || data.team } : {}),
          ...(data.department
            ? { department: Number(data.department) || data.department }
            : {}),
        };
        await updateMutation.mutateAsync({ id, payload });
      }
      onClose();
    } catch {
      // Handled by query mutation
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0">
        <DialogHeader className="p-6 pb-4 border-b border-slate-100 dark:border-slate-800">
          <DialogTitle className="text-xl font-bold text-slate-900 dark:text-white">
            Edit Profile
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500">
            Update your personal and professional details.
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col flex-1 overflow-hidden"
        >
          <div className="flex-1 overflow-y-auto p-6 space-y-8 bg-slate-50/50 dark:bg-slate-900/50">
            {/* Basic Info */}
            <section>
              <h3 className="text-sm font-bold text-slate-900 dark:text-slate-200 mb-4 uppercase tracking-wider">
                Basic Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 sm:col-span-1">
                  <TextInput
                    control={control}
                    name="first_name"
                    label="First Name"
                    required={!isFromOther}
                    disabled={isFromOther}
                    prefix={<User className="w-4 h-4 text-slate-400" />}
                    placeholder="e.g. Alex"
                  />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <TextInput
                    control={control}
                    name="last_name"
                    label="Last Name"
                    required={!isFromOther}
                    disabled={isFromOther}
                    prefix={<User className="w-4 h-4 text-slate-400" />}
                    placeholder="e.g. Morgan"
                  />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <SelectDropdown
                    control={control}
                    name="team"
                    label="Team"
                    options={teamOptions}
                    placeholder={
                      isTeamsLoading ? "Loading teams..." : "Select Team"
                    }
                    onChange={(val) => setValue("team", String(val))}
                    isSearchable
                    disabled
                    onMenuScrollToBottom={() => {
                      if (hasNextPage && !isFetchingNextPage) {
                        fetchNextPage();
                      }
                    }}
                    hasMore={hasNextPage}
                    isLoadingMore={isFetchingNextPage}
                  />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <SelectDropdown
                    control={control}
                    name="department"
                    label="Department"
                    options={departmentOptions}
                    placeholder={
                      isDepartmentsLoading
                        ? "Loading departments..."
                        : "Select Department"
                    }
                    onChange={(val) => setValue("department", String(val))}
                    isSearchable
                    disabled
                    onMenuScrollToBottom={() => {
                      if (hasDeptNextPage && !isFetchingDeptNextPage) {
                        fetchNextDeptPage();
                      }
                    }}
                    hasMore={hasDeptNextPage}
                    isLoadingMore={isFetchingDeptNextPage}
                  />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <TextInput
                    control={control}
                    name="job_role"
                    label="Job Role"
                    // disabled
                    prefix={<Briefcase className="w-4 h-4 text-slate-400" />}
                    placeholder="e.g. Senior Software Engineer"
                  />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <TextInput
                    control={control}
                    name="birth_date"
                    label="Date of Birth"
                    type="date"
                    max={getTodayDateString()}
                    prefix={<Calendar className="w-4 h-4 text-slate-400" />}
                  />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <div className="grid grid-cols-2 gap-2">
                    <TextInput
                      control={control}
                      name="experience_years"
                      label="Exp. (Years)"
                      type="number"
                      prefix={<Clock className="w-4 h-4 text-slate-400" />}
                      placeholder="0"
                    />
                    <TextInput
                      control={control}
                      name="experience_months"
                      label="Exp. (Months)"
                      type="number"
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* Location Info */}
            <section>
              <h3 className="text-sm font-bold text-slate-900 dark:text-slate-200 mb-4 uppercase tracking-wider">
                Location
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <TextInput
                    control={control}
                    name="address"
                    label="Full Address"
                    prefix={<MapPin className="w-4 h-4 text-slate-400" />}
                    placeholder="123 Main Street, Suite 400"
                  />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <TextInput
                    control={control}
                    name="city"
                    label="City"
                    prefix={<Building className="w-4 h-4 text-slate-400" />}
                    placeholder="e.g. San Francisco"
                  />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <TextInput
                    control={control}
                    name="state"
                    label="State"
                    placeholder="e.g. California"
                  />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <TextInput
                    control={control}
                    name="country"
                    label="Country"
                    prefix={<Globe className="w-4 h-4 text-slate-400" />}
                    placeholder="e.g. United States"
                  />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <TextInput
                    control={control}
                    name="postal_code"
                    label="Postal Code"
                    integerOnly
                    placeholder="e.g. 94103"
                  />
                </div>
              </div>
            </section>

            {/* Professional Summary */}
            <section>
              <h3 className="text-sm font-bold text-slate-900 dark:text-slate-200 mb-4 uppercase tracking-wider">
                Professional Summary
              </h3>
              <div className="col-span-2">
                <TextareaInput
                  control={control}
                  name="summary"
                  rows={10}
                  placeholder="Brief description of your skills, background, and career goals..."
                />
              </div>
            </section>
          </div>

          <DialogFooter className="flex items-center justify-between sm:justify-between">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={updateMutation.isPending}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              <Check className="w-4 h-4 mr-2" />{" "}
              {updateMutation.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
